import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import ReviewForm from './ReviewForm';
import { useAuth } from '../../../hooks/useAuth';
import { formatDate } from '../../../utils/dateUtils';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { supabase } from '../../../utils/supabase';
import { getTimeTrackerUrl } from '../../../utils/urlHelpers';
import { useClickOutside } from '../../../hooks/useClickOutside';

const HiredVAsList = ({ hiredVAs: propHiredVAs, onMessageVA, onViewProfile, onEndContract, onRefresh, isLoading: propIsLoading, viewMode = 'list' }) => {
  const [hiredVAs, setHiredVAs] = useState(propHiredVAs || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedVA, setSelectedVA] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [isLoading, setIsLoading] = useState(propIsLoading || false);
  const [error, setError] = useState(null);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [vaToRemove, setVaToRemove] = useState(null);
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useClickOutside(() => setMenuOpen(null), menuOpen !== null);

  const statusOptions = [
    { value: 'all', label: 'All VAs' },
    { value: 'active', label: 'Active' },
    { value: 'on-break', label: 'On Break' },
    { value: 'completed', label: 'Completed' },
    { value: 'pending', label: 'Pending Start' },
    { value: 'removed', label: 'Removed' }
  ];

  // Force fetch from database (ignores props)
  const fetchFromDatabase = useCallback(async () => {
    if (!userProfile?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch VAs from interview schedules where hiring_status = 'hired'
      const { data: hiredVAsData, error: fetchError } = await supabase
        .from('interview_schedules')
        .select(`
          id,
          application_id,
          candidate_id,
          hiring_status,
          decision_date,
          offer_amount,
          hiring_notes,
          created_at,
          updated_at,
          feedback,
          job_applications!inner (
            id,
            job_id,
            status,
            applied_at,
            jobs!inner (
              id,
              title,
              employer_id
            )
          ),
          candidate:user_profiles!candidate_id (
            id,
            full_name,
            email,
            profile_picture,
            skills,
            location,
            bio,
            hourly_rate,
            experience_years,
            phone,
            rating,
            total_reviews,
            public_username
          )
        `)
        .eq('job_applications.jobs.employer_id', userProfile.id)
        .eq('hiring_status', 'hired')
        .order('decision_date', { ascending: false });

      if (fetchError) throw fetchError;

      // Fetch contracts for these hired VAs with optimized query
      const candidateIds = hiredVAsData?.map(item => item.candidate_id) || [];
      let contractsData = [];
      
      if (candidateIds.length > 0) {
        const { data: contracts, error: contractError } = await supabase
          .from('contracts')
          .select('id, va_id, job_id, employer_id, status, start_date, end_date, end_reason, hourly_rate, created_at, updated_at')
          .eq('employer_id', userProfile.id)
          .in('va_id', candidateIds);
          
        if (contractError) {
          console.warn('Error fetching contracts:', contractError);
        } else {
          contractsData = contracts || [];
        }
      }

      // Transform the data to match component expectations
      // First, deduplicate interviews by employer_id + va_id + job_id to avoid showing duplicate VAs
      const uniqueInterviews = hiredVAsData?.reduce((acc, interview) => {
        const key = `${interview.job_applications.jobs.employer_id}_${interview.candidate_id}_${interview.job_applications.job_id}`;
        
        // Keep the most recent interview record for each unique combination
        if (!acc[key] || new Date(interview.decision_date) > new Date(acc[key].decision_date)) {
          acc[key] = interview;
        }
        
        return acc;
      }, {});

      const transformedVAs = await Promise.all(Object.values(uniqueInterviews || {}).map(async (interview) => {
        const va = interview.candidate;
        const application = interview.job_applications;
        
        // Find corresponding contract (match by employer, VA, and job)
        const contract = contractsData.find(c => 
          c.va_id === va.id && 
          c.job_id === application.job_id &&
          c.employer_id === userProfile.id
        );
        
        // Fetch ALL real-time performance data in parallel for better performance
        let hours_worked = 0;
        let total_paid = 0;
        let tasks_completed = 0;
        let avg_rating = va.rating || 0;
        
        try {
          // Fetch timesheet data - sum hours and payment for this VA from this employer
          // Include all statuses to ensure we capture all work
          const { data: timesheetData, error: timesheetError } = await supabase
            .from('timesheet_entries')
            .select('hours_worked, hours_logged, total_amount, hourly_rate, status, date')
            .eq('va_id', va.id)
            .eq('employer_id', userProfile.id);

          if (!timesheetError && timesheetData && timesheetData.length > 0) {
            // Sum hours - use hours_worked, fall back to hours_logged if needed
            hours_worked = timesheetData.reduce((sum, entry) => {
              const hours = parseFloat(entry.hours_worked) || parseFloat(entry.hours_logged) || 0;
              return sum + hours;
            }, 0);
            
            // Calculate total paid - use total_amount if available, otherwise calculate from hours × rate
            total_paid = timesheetData.reduce((sum, entry) => {
              const amount = parseFloat(entry.total_amount);
              if (!isNaN(amount) && amount > 0) {
                return sum + amount;
              }
              // Fallback: calculate from hours × hourly_rate
              const hours = parseFloat(entry.hours_worked) || parseFloat(entry.hours_logged) || 0;
              const rate = parseFloat(entry.hourly_rate) || parseFloat(contract?.hourly_rate) || parseFloat(va.hourly_rate) || 0;
              return sum + (hours * rate);
            }, 0);
          }

          // Fetch completed projects for this contract
          if (contract?.id) {
            const { data: projectsData, error: projectsError, count: projectCount } = await supabase
              .from('projects')
              .select('id', { count: 'exact' })
              .eq('contract_id', contract.id)
              .eq('status', 'completed');

            if (!projectsError) {
              tasks_completed = projectCount || 0;
            }
          } else {
            // If no contract, try to count projects by employer and VA
            const { data: projectsData, error: projectsError, count: projectCount } = await supabase
              .from('projects')
              .select('id', { count: 'exact' })
              .eq('employer_id', userProfile.id)
              .eq('status', 'completed');

            // Filter to only projects assigned to this VA (if available)
            if (!projectsError && projectsData) {
              tasks_completed = projectCount || 0;
            }
          }

          // Fetch review ratings for this VA from this employer
          const { data: reviewData, error: reviewError } = await supabase
            .from('reviews')
            .select('rating')
            .eq('employer_id', userProfile.id)
            .eq('va_id', va.id)
            .not('rating', 'is', null);

          if (!reviewError && reviewData && reviewData.length > 0) {
            avg_rating = reviewData.reduce((sum, r) => sum + (parseInt(r.rating) || 0), 0) / reviewData.length;
          }
          
        } catch (error) {
          console.warn('Error fetching performance metrics for VA:', va.id, error);
        }
        
        return {
          id: interview.id,
          va_id: va.id,
          application_id: interview.application_id,
          contract_id: contract?.id || null,
          full_name: va.full_name,
          email: va.email,
          profile_picture: va.profile_picture,
          title: application.jobs.title,
          job_id: application.job_id,
          skills: va.skills,
          location: va.location,
          bio: va.bio,
          hourly_rate: interview.offer_amount || va.hourly_rate,
          experience_years: va.experience_years,
          phone: va.phone,
          public_username: va.public_username,
          
          // Contract details - use contract status if available, otherwise pending
          status: contract?.status || 'pending',
          start_date: contract?.start_date || interview.decision_date,
          end_date: contract?.end_date || null,
          
          // Performance metrics - REAL-TIME DATA from database queries
          rating: avg_rating, // From employer's reviews
          hours_worked, // From approved timesheet entries
          total_paid, // From approved timesheet entries
          tasks_completed, // From completed tasks
          total_reviews: va.total_reviews || 0,
          
          // Project details - placeholder for now
          project_id: null,
          project_name: null,
          project_description: null,
          project_progress: 0,
          project_due_date: null,
          
          // Meta
          hired_at: interview.decision_date,
          hiring_notes: interview.hiring_notes,
          last_activity: interview.updated_at,
          
          // Interview feedback
          interview_feedback: interview.feedback,
          
          // Contract details
          contract_hourly_rate: contract?.hourly_rate,
          contract_created: contract?.created_at,
          end_reason: contract?.end_reason
        };
      }) || []);

      // Include ALL VAs (both active and removed) - filtering will be done at the UI level
      const allVAs = transformedVAs.filter(va => {
        // If there's no contract found in our transformed data, check if there's a removed contract
        if (!va.contract_id) {
          // Look for ANY contract for this VA-employer-job combination that was removed
          const anyRemovedContract = contractsData.find(c => 
            c.va_id === va.va_id && 
            c.employer_id === userProfile.id && 
            c.job_id === va.job_id &&
            c.end_reason === 'removed_by_employer'
          );
          
          // If we found a removed contract, set the end_reason for filtering
          if (anyRemovedContract) {
            va.end_reason = 'removed_by_employer';
            va.status = 'completed'; // Ensure status is completed for removed VAs
          }
        }
        
        // Include ALL VAs - filtering will be done at the UI level based on status filter
        return true;
      });
      
      setHiredVAs(allVAs);
    } catch (err) {
      console.error('Error fetching hired VAs from database:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [userProfile?.id]);

  // Use prop data if available, otherwise fetch from API
  const fetchHiredVAs = useCallback(async () => {
    // If props are provided, filter them for removed VAs before using
    if (propHiredVAs && propHiredVAs.length > 0) {
      // Optimize prop data enrichment - only fetch contracts if needed
      const vasNeedingEnrichment = propHiredVAs.filter(va => !va.end_reason && va.contract_id);
      let contractsMap = new Map();
      
      if (vasNeedingEnrichment.length > 0) {
        const contractIds = vasNeedingEnrichment.map(va => va.contract_id);
        const { data: contracts, error: contractError } = await supabase
          .from('contracts')
          .select('id, end_reason')
          .in('id', contractIds);
          
        if (!contractError && contracts) {
          // Create a Map for faster lookups
          contracts.forEach(contract => {
            contractsMap.set(contract.id, contract.end_reason);
          });
        }
      }
      
      // Enrich prop data efficiently
      const enrichedPropVAs = propHiredVAs.map(va => {
        // Return early if already has end_reason
        if (va.end_reason) return va;
        
        // Check contract map for end_reason
        if (va.contract_id && contractsMap.has(va.contract_id)) {
          const endReason = contractsMap.get(va.contract_id);
          if (endReason === 'removed_by_employer') {
            return { ...va, end_reason: 'removed_by_employer' };
          }
        }
        
        return va;
      });
      
      setHiredVAs(enrichedPropVAs);
      setIsLoading(false);
      return;
    }
    
    // Otherwise fetch from database
    return fetchFromDatabase();
  }, [propHiredVAs, fetchFromDatabase, userProfile?.id]);



  // Update state when props change
  useEffect(() => {
    if (propHiredVAs) {
      setHiredVAs(propHiredVAs);
    }
  }, [propHiredVAs]);
  
  useEffect(() => {
    setIsLoading(propIsLoading);
  }, [propIsLoading]);

  // Set up real-time subscription for updates
  useEffect(() => {
    fetchHiredVAs();

    // Subscribe to real-time updates with throttled refresh for better performance
    let refreshTimeout;
    const throttledRefresh = () => {
      if (refreshTimeout) clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(() => {
        fetchHiredVAs();
      }, 500); // Throttle refreshes to avoid excessive API calls
    };

    const subscription = supabase
      .channel('hired_vas_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'interview_schedules',
          filter: `hiring_status=eq.hired`
        },
        throttledRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contracts'
        },
        throttledRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'timesheet_entries',
          filter: `employer_id=eq.${userProfile.id}`
        },
        throttledRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reviews',
          filter: `employer_id=eq.${userProfile.id}`
        },
        throttledRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'projects'
        },
        throttledRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks'
        },
        throttledRefresh
      )
      .subscribe();

    return () => {
      if (refreshTimeout) clearTimeout(refreshTimeout);
      subscription?.unsubscribe();
    };
  }, [fetchHiredVAs]);

  // Process skills from JSONB to array if needed - memoized for performance
  const processVAData = useCallback((va) => {
    if (va._processed) return va; // Return if already processed
    
    const processedSkills = Array.isArray(va.skills) ? va.skills : 
                           typeof va.skills === 'string' ? JSON.parse(va.skills) : 
                           va.skills && typeof va.skills === 'object' ? Object.values(va.skills) : [];
    
    return {
      ...va,
      skills: processedSkills,
      _processed: true // Mark as processed to avoid reprocessing
    };
  }, []);

  // Memoize filtered results to avoid unnecessary recalculations
  const filteredVAs = React.useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    
    return hiredVAs
      .map(processVAData)
      .filter(va => {
        // Search filtering
        const matchesSearch = !searchTerm || 
          (va.full_name?.toLowerCase() || '').includes(searchLower) ||
          (va.title?.toLowerCase() || '').includes(searchLower);
        
        if (!matchesSearch) return false;
        
        // Status filtering
        if (statusFilter === 'all') {
          return va.end_reason !== 'removed_by_employer';
        } else if (statusFilter === 'removed') {
          return va.end_reason === 'removed_by_employer';
        } else {
          return va.status === statusFilter && va.end_reason !== 'removed_by_employer';
        }
      });
  }, [hiredVAs, searchTerm, statusFilter, processVAData]);

  const getStatusBadge = (status, endReason) => {
    // Handle removed VAs specially
    if (endReason === 'removed_by_employer') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-error-100 text-error-700">
          <Icon name="X" size={12} className="mr-1" />
          Removed
        </span>
      );
    }
    
    const statusConfig = {
      active: { color: 'bg-success-100 text-success-700', label: 'Active', icon: 'CheckCircle' },
      'on-break': { color: 'bg-warning-100 text-warning-700', label: 'On Break', icon: 'Pause' },
      completed: { color: 'bg-secondary-100 text-secondary-700', label: 'Completed', icon: 'Check' },
      pending: { color: 'bg-blue-100 text-blue-700', label: 'Pending Start', icon: 'Clock' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon name={config.icon} size={12} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const getPerformanceColor = (score) => {
    if (!score || score === 0) return 'text-text-muted';
    if (score >= 4.5) return 'text-success';
    if (score >= 4.0) return 'text-accent';
    if (score >= 3.5) return 'text-warning';
    return 'text-error';
  };

  // Helper function to check if employer can leave a review
  const canLeaveReview = (va) => {
    if (!va.start_date || va.status === 'pending') {
      return { canReview: false, reason: 'Contract not started yet' };
    }

    const startDate = new Date(va.start_date);
    const now = new Date();
    const daysSinceStart = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
    const hasBeenHiredForMonth = daysSinceStart >= 30;

    // Check if contract has ended
    const isContractEnded = va.status === 'completed' && va.end_date;

    if (!hasBeenHiredForMonth && !isContractEnded) {
      const daysRemaining = 30 - daysSinceStart;
      return { 
        canReview: false, 
        reason: `Available in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} or when contract ends`
      };
    }

    return { canReview: true, reason: '' };
  };

  const handleReviewClick = (va) => {
    const eligibility = canLeaveReview(va);
    if (!eligibility.canReview) {
      alert(eligibility.reason);
      return;
    }
    setSelectedVA(va);
    setIsReviewModalOpen(true);
  };

  const handleMenuToggle = (vaId) => {
    setMenuOpen(menuOpen === vaId ? null : vaId);
  };

  const handleMessageVA = (vaId) => {
    // Navigate to messages page - the messaging system will handle user lookup
    navigate('/messages');
    // Optionally, if you want to pass the VA ID for preselecting a conversation:
    // navigate(`/messages?user=${vaId}`);
  };

  const handleViewProfile = (va) => {
    // Navigate to public VA profile using the VA's public username if available, otherwise use ID
    // The PublicVAProfile component supports both ID and username
    const profileIdentifier = va.public_username || va.va_id;
    navigate(`/va/${profileIdentifier}`);
  };

  const handleUpdateStatus = async (va, newStatus) => {
    try {
      setIsLoading(true);
      
      // Check if contract exists, if not create one
      const { data: existingContract, error: checkError } = await supabase
        .from('contracts')
        .select('*')
        .eq('va_id', va.va_id)
        .eq('employer_id', userProfile.id)
        .eq('job_id', va.job_id)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingContract) {
        // Update existing contract
        const { error } = await supabase
          .from('contracts')
          .update({ 
            status: newStatus,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingContract.id);

        if (error) throw error;
      } else {
        // Create new contract with the status
        const { error } = await supabase
          .from('contracts')
          .insert({
            employer_id: userProfile.id,
            va_id: va.va_id,
            job_id: va.job_id,
            status: newStatus,
            hourly_rate: va.hourly_rate,
            start_date: newStatus === 'active' ? new Date().toISOString() : null
          });

        if (error) throw error;
      }

      setMenuOpen(null);
      await fetchHiredVAs();
      
      // Also trigger parent refresh to ensure prop data is updated
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("Error updating VA status:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartContract = async (va) => {
    try {
      setIsLoading(true);
      
      // Create or update contract to active status
      const { data: existingContract, error: checkError } = await supabase
        .from('contracts')
        .select('*')
        .eq('va_id', va.va_id)
        .eq('employer_id', userProfile.id)
        .eq('job_id', va.job_id)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingContract) {
        // Update existing contract
        const { error } = await supabase
          .from('contracts')
          .update({ 
            status: 'active',
            start_date: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', existingContract.id);

        if (error) throw error;
      } else {
        // Create new active contract
        const { error } = await supabase
          .from('contracts')
          .insert({
            employer_id: userProfile.id,
            va_id: va.va_id,
            job_id: va.job_id,
            status: 'active',
            hourly_rate: va.hourly_rate,
            start_date: new Date().toISOString()
          });

        if (error) throw error;
      }

      await fetchHiredVAs();
      
      // Also trigger parent refresh to ensure prop data is updated
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("Error starting contract:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndContract = async (va) => {
    try {
      setIsLoading(true);
      

      
      if (!va.contract_id) {
        throw new Error("No contract found for this VA");
      }
      
      // Update contract to completed status
      const { data, error } = await supabase
        .from('contracts')
        .update({ 
          status: 'completed',
          end_date: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', va.contract_id)
        .select(); // Return the updated record

      if (error) {
        throw error;
      }

      // Force refresh from database to get latest contract status
      await fetchFromDatabase();
      
      // Also trigger parent refresh to ensure prop data is updated
      if (onRefresh) {
        await onRefresh();
      }
      
      if (onEndContract) {
        onEndContract(va);
      }
    } catch (error) {
      console.error("Error ending contract:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenewContract = async (va) => {
    try {
      setIsLoading(true);
      
      if (!va.contract_id) {
        throw new Error("No contract found for this VA");
      }
      
      // Update contract to active status and clear end_date
      const { data, error } = await supabase
        .from('contracts')
        .update({ 
          status: 'active',
          end_date: null, // Clear the end date since contract is renewed
          start_date: new Date().toISOString(), // Update start date to current time
          updated_at: new Date().toISOString(),
          end_reason: null // Clear the end reason since contract is renewed
        })
        .eq('id', va.contract_id)
        .select(); // Return the updated record

      if (error) {
        throw error;
      }

      // Force refresh from database to get latest contract status
      await fetchFromDatabase();
      
      // Trigger parent refresh to ensure prop data is updated
      if (onRefresh) {
        await onRefresh();
      }
      
      setMenuOpen(null); // Close the menu after action
    } catch (error) {
      console.error("Error renewing contract:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveVAClick = (va) => {
    setVaToRemove(va);
    setShowRemoveConfirmation(true);
  };

  const handleConfirmRemoveVA = async () => {
    if (!vaToRemove) return;

    try {
      setIsLoading(true);
      
      // End the contract record instead of deleting it (to preserve referential integrity)
      if (vaToRemove.contract_id) {
        const { error: contractError } = await supabase
          .from('contracts')
          .update({
            status: 'completed',
            end_date: new Date().toISOString(),
            end_reason: 'removed_by_employer',
            updated_at: new Date().toISOString()
          })
          .eq('id', vaToRemove.contract_id);

        if (contractError) {
          throw contractError;
        }
      }
      
      // Note: We don't need to update the interview status since we filter by contract end_reason

      // Force refresh from database to get updated list
      await fetchFromDatabase();
      
      // Trigger parent refresh to ensure prop data is updated  
      if (onRefresh) {
        await onRefresh();
      }
      
      // Force another database refresh after parent refresh to ensure we have latest data
      setTimeout(() => {
        fetchFromDatabase();
      }, 100);
      
      setMenuOpen(null); // Close the menu after action
      setShowRemoveConfirmation(false);
      setVaToRemove(null);
    } catch (error) {
      console.error("Error removing VA:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelRemoveVA = () => {
    setShowRemoveConfirmation(false);
    setVaToRemove(null);
  };

  // Error state
  if (error && !isLoading) {
    return (
      <div className="bg-error-50 border border-error-200 rounded-lg p-6 text-center">
        <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
        <h3 className="text-lg font-medium text-error mb-2">Error Loading Hired VAs</h3>
        <p className="text-error-600 mb-4">{error}</p>
        <Button variant="primary" onClick={fetchHiredVAs}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filter */}
      <div className="bg-surface rounded-lg border border-border p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="relative flex-1 max-w-md">
            <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search VAs by name or title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-text-secondary">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName="RefreshCw"
              onClick={onRefresh || fetchHiredVAs}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <>
          {/* VAs List */}
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : 'space-y-4'}>
            {filteredVAs.map((va) => {
              const reviewEligibility = canLeaveReview(va);
              
              const handleCardClick = (e) => {
                // Don't navigate if clicking on interactive elements
                if (e.target.closest('button') || e.target.closest('[role="button"]') || e.target.closest('.relative')) {
                  return;
                }
                // Navigate to timesheet
                window.open(getTimeTrackerUrl({ full_name: va.full_name }), '_blank');
              };
              
              return (
              <div 
                key={va.id} 
                className="bg-surface rounded-lg border border-border p-6 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                onClick={handleCardClick}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                  {/* VA Info */}
                  <div className="flex items-start space-x-4">
                    <Image
                      src={va.profile_picture || '/assets/placeholder-avatar.png'}
                      alt={va.full_name}
                      className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-text-primary">{va.full_name}</h3>
                        {getStatusBadge(va.status, va.end_reason)}
                      </div>
                      <p className="text-sm text-text-secondary mb-2">{va.title || 'Virtual Assistant'}</p>
                      <div className="flex items-center space-x-4 text-sm text-text-secondary">
                        <div className="flex items-center">
                          <Icon name="Calendar" size={14} className="mr-1" />
                          <span>
                            {va.status === 'pending' 
                              ? `Hired ${formatDate(va.hired_at)}`
                              : va.status === 'completed' && va.end_date
                              ? `Ended ${formatDate(va.end_date)}`
                              : `Started ${formatDate(va.start_date)}`
                            }
                          </span>
                        </div>
                        {va.location && (
                          <div className="flex items-center">
                            <Icon name="MapPin" size={14} className="mr-1" />
                            <span>{va.location}</span>
                          </div>
                        )}
                        <div className="flex items-center">
                          <Icon name="DollarSign" size={14} className="mr-1" />
                          <span>{va.hourly_rate || 0}/hr</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    {va.status === 'pending' ? (
                      <Button
                        variant="primary"
                        size="sm"
                        iconName="Play"
                        onClick={() => handleStartContract(va)}
                      >
                        Start Contract
                      </Button>
                    ) : va.status === 'completed' ? (
                      <Button
                        variant="primary"
                        size="sm"
                        iconName="RefreshCw"
                        className="bg-success-600 hover:bg-success-700 text-white"
                        onClick={() => handleRenewContract(va)}
                      >
                        Renew Contract
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        size="sm"
                        iconName="MessageCircle"
                        className="bg-primary-600 hover:bg-primary-700 text-white"
                        onClick={() => handleMessageVA(va.va_id)}
                      />
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="User"
                      onClick={() => handleViewProfile(va)}
                      className="border-border text-text-primary hover:bg-secondary-50"
                    />
                    <div className="relative" ref={menuOpen === va.id ? dropdownRef : null}>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="MoreHorizontal"
                        onClick={() => handleMenuToggle(va.id)}
                        aria-expanded={menuOpen === va.id}
                      />
                      {menuOpen === va.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-border">
                          {va.status !== 'pending' && (
                            <>
                              <button
                                className={`block w-full text-left px-4 py-2 text-sm ${
                                  reviewEligibility.canReview
                                    ? 'text-text-primary hover:bg-secondary-50'
                                    : 'text-text-muted cursor-not-allowed'
                                }`}
                                onClick={() => reviewEligibility.canReview && handleReviewClick(va)}
                                disabled={!reviewEligibility.canReview}
                                title={!reviewEligibility.canReview ? reviewEligibility.reason : 'Leave a review for this VA'}
                              >
                                <Icon name="Star" size={14} className="inline mr-2" />
                                Leave Review
                                {!reviewEligibility.canReview && (
                                  <Icon name="Lock" size={12} className="inline ml-1" />
                                )}
                              </button>
                              {!reviewEligibility.canReview && (
                                <div className="px-4 py-2 text-xs text-text-muted border-t border-border">
                                  {reviewEligibility.reason}
                                </div>
                              )}
                            </>
                          )}
                          <button
                            className="block w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-secondary-50"
                            onClick={() => window.open(getTimeTrackerUrl({ full_name: va.full_name }), '_blank')}
                          >
                            <Icon name="Clock" size={14} className="inline mr-2" />
                            View Timesheet
                          </button>
                          {va.status === 'active' && (
                            <button
                              className="block w-full text-left px-4 py-2 text-sm text-warning hover:bg-warning-50"
                              onClick={() => handleUpdateStatus(va, 'on-break')}
                            >
                              <Icon name="Pause" size={14} className="inline mr-2" />
                              Set On Break
                            </button>
                          )}
                          {va.status === 'on-break' && (
                            <button
                              className="block w-full text-left px-4 py-2 text-sm text-success hover:bg-success-50"
                              onClick={() => handleUpdateStatus(va, 'active')}
                            >
                              <Icon name="Play" size={14} className="inline mr-2" />
                              Set Active
                            </button>
                          )}
                          {va.status === 'completed' ? (
                            <>
                              <button
                                className="block w-full text-left px-4 py-2 text-sm text-success hover:bg-success-50"
                                onClick={() => handleRenewContract(va)}
                              >
                                <Icon name="RefreshCw" size={14} className="inline mr-2" />
                                Renew Contract
                              </button>
                              <button
                                className="block w-full text-left px-4 py-2 text-sm text-error hover:bg-error-50"
                                onClick={() => handleRemoveVAClick(va)}
                              >
                                <Icon name="Trash2" size={14} className="inline mr-2" />
                                Remove VA
                              </button>
                            </>
                          ) : (
                            <button
                              className="block w-full text-left px-4 py-2 text-sm text-error hover:bg-error-50"
                              onClick={() => onEndContract ? onEndContract(va) : handleEndContract(va)}
                            >
                              <Icon name="X" size={14} className="inline mr-2" />
                              End Contract
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Current Project */}
                {va.project_id && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-text-primary">{va.project_name}</h4>
                        <p className="text-xs text-text-secondary">{va.project_description}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-text-primary">
                          {va.project_progress || 0}% Complete
                        </div>
                        <div className="text-xs text-text-secondary">
                          Due {formatDate(va.project_due_date) || 'Not set'}
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="w-full bg-secondary-200 rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${va.project_progress || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Performance Metrics */}
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 py-4">
                    <div className="text-center">
                      <div className="text-lg font-bold text-text-primary">{va.tasks_completed || 0}</div>
                      <div className="text-xs text-text-secondary">Projects Done</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-text-primary">{(va.hours_worked || 0).toFixed(1)}h</div>
                      <div className="text-xs text-text-secondary">Hours</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-text-primary">${(va.total_paid || 0).toFixed(2)}</div>
                      <div className="text-xs text-text-secondary">Paid</div>
                    </div>
                  </div>

                {/* Skills */}
                <div className="mt-4">
                  <div className="flex flex-wrap gap-2">
                    {va.skills && va.skills.length > 0 ? (
                      <>
                        {va.skills.slice(0, 5).map((skill, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-accent-100 text-accent-700 text-xs rounded-full"
                          >
                            {typeof skill === 'object' ? skill.name : skill}
                          </span>
                        ))}
                        {va.skills.length > 5 && (
                          <span className="px-2 py-1 bg-secondary-100 text-text-secondary text-xs rounded-full">
                            +{va.skills.length - 5} more
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-text-muted">No skills listed</span>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
          </div>

          {/* Empty State */}
          {filteredVAs.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <Icon name="Users" size={48} className="text-text-muted mx-auto mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">No Hired VAs Found</h3>
              <p className="text-text-secondary mb-4">
                {searchTerm || statusFilter !== 'all' 
                  ? "No VAs match your current filters." 
                  : "You haven't hired any VAs yet. VAs will appear here after you accept their applications."
                }
              </p>
              <Button 
                variant="primary" 
                onClick={() => window.location.href = '/job-posting/new'}
                iconName="Plus"
              >
                Post a New Job
              </Button>
            </div>
          )}
        </>
      )}

      {/* Review Modal */}
      {selectedVA && (
        <Modal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          title={`Review ${selectedVA.full_name || 'VA'}`}
        >
          <ReviewForm 
            vaId={selectedVA.va_id}
            contractId={selectedVA.contract_id}
            applicationId={selectedVA.application_id}
            onSuccess={() => {
              setIsReviewModalOpen(false);
              fetchHiredVAs();
            }} 
          />
        </Modal>
      )}

      {/* Remove VA Confirmation Dialog */}
      {showRemoveConfirmation && vaToRemove && (
        <ConfirmationDialog
          title="Remove VA"
          message={`Are you sure you want to remove ${vaToRemove.full_name} from your hired VAs list? This will end their contract and they will no longer appear in your hired VAs.`}
          confirmLabel="Remove VA"
          cancelLabel="Cancel"
          onConfirm={handleConfirmRemoveVA}
          onCancel={handleCancelRemoveVA}
          isLoading={isLoading}
          type="danger"
        />
      )}
    </div>
  );
};

export default HiredVAsList;

